// What changed in a building, as its change stream reports it (docs/reference/event-catalog.md),
// reduced to the three things an open screen refreshes for. One event bus on `window`, like the
// `kairos:login` signal the inventory filter already listens to: the stream connection publishes,
// and any view subscribes for the kinds it shows without knowing about the stream.

import { useEffect, useState } from "react";

export type BuildingChangeKind = "findings" | "telemetry" | "learning";

const CHANGE_EVENT = "kairos:building-changed";

/** Which refresh an event type calls for, or null for events no screen shows (e.g. the type
 *  registry, which only appears on the estate stream anyway). */
export function changeKindOfEventType(type: string): BuildingChangeKind | null {
  if (type.startsWith("kairos.finding.")) return "findings";
  if (
    type === "kairos.samples.ingested.v1" ||
    type === "kairos.derived.ingested.v1"
  )
    return "telemetry";
  if (type === "kairos.learning.updated.v1") return "learning";
  return null;
}

interface BuildingChangeDetail {
  buildingId: string;
  kind: BuildingChangeKind;
}

export function publishBuildingChange(
  buildingId: string,
  kind: BuildingChangeKind,
): void {
  window.dispatchEvent(
    new CustomEvent<BuildingChangeDetail>(CHANGE_EVENT, {
      detail: { buildingId, kind },
    }),
  );
}

export function subscribeBuildingChanges(
  listener: (buildingId: string, kind: BuildingChangeKind) => void,
): () => void {
  const handle = (event: Event) => {
    const { buildingId, kind } = (event as CustomEvent<BuildingChangeDetail>)
      .detail;
    listener(buildingId, kind);
  };
  window.addEventListener(CHANGE_EVENT, handle);
  return () => window.removeEventListener(CHANGE_EVENT, handle);
}

/**
 * A counter that goes up each time `buildingId` reports a change of `kind`. Add it to the
 * dependencies of the effect that loads the data, the way a `refreshKey` is used: the effect
 * re-runs and the view shows the new state. Stays 0 without a building.
 */
export function useBuildingChangeTick(
  buildingId: string | null,
  kind: BuildingChangeKind,
): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (buildingId === null) return;
    return subscribeBuildingChanges((changed, changedKind) => {
      if (changed === buildingId && changedKind === kind) setTick((t) => t + 1);
    });
  }, [buildingId, kind]);
  return tick;
}
